import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingBag, Star, Coins, Check } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { CREDIT_PACKS, formatCredits, formatVnd, tokensToPages } from '../../../services/mock/creditMock';
import { usePageMeta } from '../../../hooks/usePageMeta';

const PricingPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  usePageMeta(
    'Bảng giá | Dịch Tài Liệu — Từ 19.000đ/gói',
    'Gói credit từ 19.000đ. Rẻ hơn dịch giả 50-150 lần. Thử miễn phí 200.000 credits khi đăng ký (đủ 1 báo cáo dày complete).',
    'https://dichtailieu.vn/pricing',
  );

  const handleCtaClick = () => {
    if (user) navigate('/app/shop');
    else navigate('/login');
  };

  return (
    <div className="min-h-screen bg-white">
      <nav className="px-6 py-4 border-b border-slate-100 bg-white sticky top-0 z-10">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-emerald-600 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Về trang chủ</span>
          </Link>
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-emerald-600" />
            <span className="font-black text-slate-800">Bảng giá</span>
          </div>
        </div>
      </nav>

      <section className="px-6 pt-16 pb-12 text-center max-w-3xl mx-auto">
        <h1 className="text-3xl sm:text-4xl font-black text-slate-900 mb-4 tracking-tight">Giá cực rẻ</h1>
        <p className="text-base sm:text-lg text-slate-500">
          Từ 443đ/trang. Rẻ hơn dịch giả 120 lần. Giữ format DOCX/PPTX 100%.
        </p>
        <p className="text-xs text-slate-400 mt-3">
          Số trang ước tính dựa trên file dày trung bình (báo cáo/audit/hợp đồng). File ngắn dày ít hơn = nhiều trang hơn. Phí cuối tính theo token thật của AI.
        </p>
      </section>

      <section className="max-w-5xl mx-auto px-6 pb-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-10">
          {CREDIT_PACKS.map((pack) => {
            const isPopular = pack.isPopular;
            return (
              <div
                key={pack.id}
                className={`relative bg-white rounded-2xl p-6 ${isPopular ? 'border-2 border-emerald-500 shadow-sm' : 'border border-slate-200'}`}
              >
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-[11px] font-bold px-3 py-1 rounded-full flex items-center gap-1 shadow-sm whitespace-nowrap">
                    <Star className="w-3 h-3 fill-white" /> Phổ biến
                  </div>
                )}

                <h3 className="text-lg font-bold text-slate-800 mb-1">
                  {pack.name}
                </h3>
                <p className="text-xs text-slate-500 mb-4 min-h-[32px]">
                  {pack.description}
                </p>

                <div className="mb-2">
                  <div className="text-2xl sm:text-3xl font-black text-emerald-600">
                    {formatVnd(pack.priceVnd)}
                  </div>
                </div>

                <div className="text-xs text-slate-500 mb-4">
                  {formatCredits(pack.credits)} credits (~{tokensToPages(pack.credits)} trang)
                </div>

                {pack.savingsPercent > 0 && (
                  <div className="inline-block bg-emerald-50 text-emerald-600 text-xs font-bold px-2.5 py-1 rounded-full">
                    Tiết kiệm {pack.savingsPercent}%
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="text-center">
          <button
            onClick={handleCtaClick}
            className="px-8 py-3.5 bg-emerald-600 text-white font-bold rounded-xl hover:bg-emerald-700 shadow-md shadow-emerald-600/20 transition-all active:scale-95 inline-flex items-center justify-center text-sm sm:text-base"
          >
            {user ? 'Vào app ngay' : 'Bắt đầu miễn phí — 200K credits tặng'}
          </button>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 pb-24">
        <h2 className="text-2xl font-black text-slate-900 mb-6 text-center">Câu hỏi thường gặp</h2>
        <div className="space-y-4">
          {[
            {
              q: 'Credit là gì? Tính thế nào?',
              a: 'Credit được tính theo lượng nội dung AI xử lý. Khoảng 75,000 credits dịch được 20 trang full chữ. Gói Vừa 1.5M credits dịch được ~400 trang.',
            },
            {
              q: 'Credit có hết hạn không?',
              a: 'Trong giai đoạn beta, credit không hết hạn. Chúng tôi sẽ thông báo trước 30 ngày nếu thay đổi.',
            },
            {
              q: 'Thanh toán ra sao?',
              a: 'Chuyển khoản ngân hàng qua VietQR hoặc STK. Tự động cộng credit sau 1 giây – 1 phút khi email ngân hàng đến (24/7). Nếu sau 5 phút chưa cộng, email support@dichtailieu.vn kèm mã chuyển khoản.',
            },
            {
              q: 'Hoàn tiền được không?',
              a: (
                <>
                  Credit đã thanh toán không hoàn lại nếu bạn đổi ý. Có hoàn cho lỗi hệ thống / chuyển trùng / chuyển dư (3-7 ngày làm việc). Chi tiết xem <Link to="/refund" className="text-emerald-600 hover:underline">Chính sách hoàn tiền</Link>.
                </>
              ),
            },
            {
              q: 'Bảo mật tài liệu?',
              a: (
                <>
                  Nội dung tài liệu KHÔNG được lưu để khôi phục sau khi dịch xong. Chế độ Ẩn Danh không lưu pending_sync. Xem <Link to="/privacy" className="text-emerald-600 hover:underline">Chính sách bảo mật</Link>.
                </>
              ),
            },
          ].map((item, i) => (
            <details key={i} className="group bg-white rounded-xl border border-slate-200 overflow-hidden">
              <summary className="px-5 py-4 cursor-pointer font-bold text-slate-800 hover:bg-slate-50 flex items-center justify-between">
                {item.q}
                <span className="text-emerald-500 group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <div className="px-5 pb-4 text-slate-600 leading-relaxed">{item.a}</div>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
};

export default PricingPage;
